import { Box, Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { JButton } from '@atoms/JButton';
import { Loading } from '@atoms/Loading';
import Paths from '@/routes/paths';
import Role from '@authentication/models/role';
import { TopBar } from '@/layouts/TopBar';
import UserFormContact from '../UserFormContact';
import UserFormDetails from '../UserFormDetails';
import UserFormDetailsData from '../../models/user-form-details-data';
import UserFormRole from '../UserFormRole';
import UserFormRolesData from '../../models/user-form-roles-data';
import UserItemDto from '../../models/dto/user-item.dto';
import { useEditUserRequest } from '../../mutations/userMutations';
import { useGetUserById } from '../../queries/useGetUsers';
import useJSnackbar from '@/hooks/useJSnackbar';
import { useTranslation } from 'react-i18next';
import useUserMutationContext from '../../hooks/useUserMutationContext';

export const EditUserForm = () => {
  const { id } = useParams();
  const { data, isSuccess, isLoading } = useGetUserById(id, !!id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSnackbar } = useJSnackbar();
  const { setPayload, payload, isDirty } = useUserMutationContext();
  const [submit, setSubmit] = useState(false);
  const user = data?.data;
  const { setMode } = useUserMutationContext();
  const {
    mutate: editUser,
    isSuccess: isEditSuccess,
    isLoading: isEditLoading,
    isError: isEditError,
  } = useEditUserRequest();

  const [localPayload, setLocalPayload] = useState<
    UserFormDetailsData | undefined
  >();
  const [localPayloadRole, setLocalPayloadRole] = useState<
    UserFormRolesData | undefined
  >();

  useEffect(() => {
    setMode('edit');
  }, []);
  useEffect(() => {
    if (!submit || !payload) return;
    handleSave();
    setSubmit(false);
  }, [submit, payload]);
  const fillForm = () => {
    if (!user) return;
    const payloadForm: UserItemDto = {
      userDto: {
        firstname: user?.firstname,
        lastname: user?.lastname,
        email: user?.email,
        mobilePhone: user?.mobilePhone ?? undefined,
        phone: user?.phone ?? undefined,
      },
      userRolesEnterpriseDto: user.userRoles
        .filter((item) => item.establishment === null)
        .map((item) => ({
          enterpriseId: item.enterprise.id,
          role: item.role,
          enterpriseName: item.enterprise.name,
        }))[0],
      userRolesEstablishmentDto: user.userRoles
        .filter(
          (item) =>
            item.role !== Role.ADMIN_ENTERPRISE && item.establishment !== null,
        )
        .filter((item) => !!item.establishment)

        .map((item) => {
          return {
            enterpriseId: item.enterprise.id,
            enterpriseName: item.enterprise.name,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            establishmentId: item.establishment!.id,
            establishmentName: item.establishment?.name,
            role: item.role,
          };
        }),
    };
    setPayload(payloadForm);
  };
  useEffect(() => {
    if (!isSuccess) return;
    fillForm();
  }, [isSuccess]);

  type valuesProp = UserFormDetailsData | UserFormRolesData;
  const setDtoValues = async (dataValues: valuesProp) => {
    if ('firstname' in dataValues) {
      setPayload((prev) => {
        return {
          userDto: dataValues,
          userRolesEstablishmentDto: prev?.userRolesEstablishmentDto,
          userRolesEnterpriseDto: prev?.userRolesEnterpriseDto,
        };
      });
    }

    if (
      'userRolesEstablishment' in dataValues &&
      dataValues.userRolesEstablishment
    ) {
      setPayload((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          userRolesEstablishmentDto: dataValues.userRolesEstablishment,
        };
      });
    }
    if ('userRolesEnterprise' in dataValues && dataValues.userRolesEnterprise) {
      setPayload((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          userRolesEnterpriseDto: dataValues.userRolesEnterprise,
        };
      });
    }
  };
  const handleSave = () => {
    if (!payload) return;
    if (payload.userRolesEnterpriseDto) {
      editUser({ payload, userType: 'enterprise', id });
    }
    if (
      payload.userRolesEstablishmentDto &&
      payload.userRolesEstablishmentDto.length > 0
    )
      editUser({
        payload,
        userType: 'establishment',
        id,
      });
  };

  useEffect(() => {
    if (!isEditSuccess) return;
    navigate(Paths.UsersPage);
    showSnackbar(t('USER_UPDATE_SUCCESS'), 'success');
  }, [isEditSuccess]);
  useEffect(() => {
    if (isEditError) {
      showSnackbar(t('API_ERROR'), 'error');
      setSubmit(false);
    }
  }, [isEditError]);
  if (isLoading || isEditLoading) return <Loading />;
  return (
    <>
      <TopBar title={t('EDIT_USER')}>
        <Stack direction={'row'} gap={2}>
          <JButton onClick={() => navigate(Paths.UsersPage)} variant="text">
            {t('CANCEL')}
          </JButton>
          {isDirty && (
            <JButton
              onClick={() => {
                if (localPayload) setDtoValues(localPayload);
                if (localPayloadRole) setDtoValues(localPayloadRole);
                setSubmit(true);
              }}
              size="small"
              variant="contained"
              color="secondary"
            >
              {t('SAVE')}
            </JButton>
          )}
        </Stack>
      </TopBar>
      <Box padding={3}>
        <Grid container gap={3}>
          <Grid item xs={3.8}>
            <UserFormDetails
              handleChange={(value) => {
                setLocalPayload(value);
              }}
            />
          </Grid>
          <Grid item xs={3.8}>
            <UserFormRole
              handleChange={(value) => {
                setLocalPayloadRole(value);
              }}
            />
          </Grid>
          <Grid item xs={3.8}>
            <UserFormContact
              handleChange={(value) => {
                setLocalPayload(value);
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
